import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, InputAdornment, MenuItem, Pagination, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdBlock, MdCheckCircle, MdClose, MdInventory2, MdRefresh, MdSearch, MdVisibility } from 'react-icons/md';
import { getData, postData } from '../Webservice/Webservice';
import { API_URL } from '../Utils/url';

const FONT = 'Mulish, sans-serif';
const LIMIT = 15;
const QUICK_REASONS = ['Price is too high', 'Price is below our minimum', 'Product is not available to resell', 'Seller details need verification'];

const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const img = (p) => (p ? (p.startsWith('http') ? p : `${API_URL.replace(/\/api\/?$/, '/')}${p.replace(/^\//, '')}`) : undefined);
const inr = (v) => (v === undefined || v === null || v === '' ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
const dt = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } };
const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
const cell = { fontFamily: FONT, fontSize: 13.5 };
const iconBtn = (c) => ({ color: c, background: `${c}14`, borderRadius: '8px', '&:hover': { background: `${c}26` }, '&.Mui-disabled': { color: '#cbd5e1', background: 'transparent' } });

const STATUS = {
  pending: { label: 'Pending', bg: '#fef3c7', fg: '#92400e', accent: '#f59e0b' },
  approved: { label: 'Approved', bg: '#dcfce7', fg: '#166534', accent: '#22c55e' },
  rejected: { label: 'Rejected', bg: '#fee2e2', fg: '#991b1b', accent: '#ef4444' },
};
const StatusChip = ({ status }) => {
  const s = STATUS[status] || STATUS.pending;
  return <Chip size="small" label={s.label} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: s.bg, color: s.fg }} />;
};

const Kpi = ({ title, value, from, to, active, onClick }) => (
  <Box onClick={onClick} sx={{ flex: 1, minWidth: 150, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, cursor: onClick ? 'pointer' : 'default', outline: active ? '3px solid #0f172a' : 'none', outlineOffset: 2 }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>{value}</Typography>
  </Box>
);

const Card = ({ title, accent, children }) => (
  <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2.25 }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1 }}>{title}</Typography>
    {children}
  </Box>
);
const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.95, borderBottom: '1px dashed #e2e8f0' }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{label}</Typography>
    <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{value || value === 0 ? value : '—'}</Typography>
  </Box>
);

// requested price against what the catalogue already shows
const Delta = ({ asked, current }) => {
  if (!current || !asked) return null;
  const pct = Math.round(((asked - current) / current) * 100);
  if (pct === 0) return <span style={{ color: '#94a3b8', fontSize: 12 }}>same</span>;
  return <span style={{ color: pct > 0 ? '#b45309' : '#166534', fontWeight: 700, fontSize: 12 }}>{pct > 0 ? '+' : ''}{pct}%</span>;
};

const EMPTY = { status: 'pending', from: '', to: '' };

const ProductRequestList = () => {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filters, setFilters] = useState(EMPTY);
  const seq = useRef(0);

  const [viewing, setViewing] = useState(null);
  const [decision, setDecision] = useState(null); // { request, status }
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: LIMIT, search: debounced, ...filters };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData('product/seller-assign-request/list', params, authToken(), false);
      if (mine !== seq.current) return;
      setRows(Array.isArray(res?.request) ? res.request : []);
      setMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
      if (res?.stats) setStats(res.stats);
    } catch {
      if (mine === seq.current) setError('Could not load product requests.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, debounced, filters]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const pickStatus = (s) => setF('status', filters.status === s ? '' : s);
  const dirty = !!search || filters.status !== 'pending' || !!filters.from || !!filters.to;

  const openDecision = (request, status) => {
    setViewing(null);
    setDecision({ request, status });
    setReason('');
    setReasonError('');
    setActionError('');
  };

  const confirm = async () => {
    setActionError('');
    if (decision.status === 'rejected' && !reason.trim()) return setReasonError('Tell the seller why it was rejected.');
    setSubmitting(true);
    try {
      await postData('product/seller-assign-approve', {
        requestId: decision.request._id,
        status: decision.status,
        ...(decision.status === 'rejected' ? { rejectionReason: reason.trim() } : {}),
      }, authToken(), false);
      setNotice(decision.status === 'approved' ? `Approved. ${decision.request.createdBy?.name || 'The seller'} can now sell this product.` : 'Request rejected. The seller has been told why.');
      setDecision(null);
      load();
    } catch (err) {
      setActionError(err?.message || 'Could not update the request.');
    } finally {
      setSubmitting(false);
    }
  };

  const open = viewing;
  const p = open?.product_id || {};
  const seller = open?.createdBy || {};

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Product requests</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Sellers asking to sell a product from your catalogue at their own prices.</Typography>
        </Box>
        <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={loading} sx={{ background: '#f1f5f9', borderRadius: '10px' }}><MdRefresh size={20} /></IconButton></span></Tooltip>
      </Box>

      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi title="Waiting for a decision" value={stats.pending} from="#f59e0b" to="#b45309" active={filters.status === 'pending'} onClick={() => pickStatus('pending')} />
        <Kpi title="Approved" value={stats.approved} from="#22c55e" to="#15803d" active={filters.status === 'approved'} onClick={() => pickStatus('approved')} />
        <Kpi title="Rejected" value={stats.rejected} from="#ef4444" to="#b91c1c" active={filters.status === 'rejected'} onClick={() => pickStatus('rejected')} />
        <Kpi title="All requests" value={stats.total} from="#6366f1" to="#4338ca" active={filters.status === ''} onClick={() => setF('status', '')} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search product, UPC, seller or phone…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Status" value={filters.status} onChange={(e) => setF('status', e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="pending">Pending</MenuItem><MenuItem value="approved">Approved</MenuItem><MenuItem value="rejected">Rejected</MenuItem>
        </TextField>
        <TextField size="small" type="date" label="Requested from" value={filters.from} onChange={(e) => setF('from', e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="Requested to" value={filters.to} onChange={(e) => setF('to', e.target.value)} InputLabelProps={{ shrink: true }} />
        {dirty && <Button onClick={() => { setSearch(''); setFilters(EMPTY); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Product</TableCell>
                <TableCell sx={head}>Seller</TableCell>
                <TableCell sx={head} align="right">Asked B2C price</TableCell>
                <TableCell sx={head} align="right">Asked B2B price</TableCell>
                <TableCell sx={head}>Requested</TableCell>
                <TableCell sx={head}>Status</TableCell>
                <TableCell sx={head} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(6)].map((_, i) => <TableRow key={i}>{[...Array(7)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>{filters.status === 'pending' && !search ? 'No requests are waiting. Nice work.' : 'No requests match these filters.'}</TableCell></TableRow>}
              {!loading && rows.map((r) => {
                const prod = r.product_id || {};
                const pending = r.status === 'pending';
                return (
                  <TableRow key={r._id} hover sx={{ cursor: 'pointer' }} onClick={() => setViewing(r)}>
                    <TableCell sx={{ ...cell, maxWidth: 280 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Avatar variant="rounded" src={img(prod.product_images?.[0])} sx={{ width: 40, height: 40, bgcolor: '#e0e7ff', color: '#3730a3' }}><MdInventory2 size={18} /></Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }} title={prod.title}>{prod.title || 'Product removed'}</Typography>
                          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>UPC {prod.upc || 'N/A'}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={cell}>
                      <b>{r.createdBy?.name || '—'}</b>
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{r.createdBy?.business_name || r.createdBy?.mobile || ''}</Typography>
                    </TableCell>
                    <TableCell sx={cell} align="right">{inr(r.b2c_sellingPrice)} <Delta asked={r.b2c_sellingPrice} current={prod.b2c_sellingPrice} /></TableCell>
                    <TableCell sx={cell} align="right">{inr(r.b2b_sellingPrice)} <Delta asked={r.b2b_sellingPrice} current={prod.b2b_sellingPrice} /></TableCell>
                    <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>{dt(r.createdAt)}</TableCell>
                    <TableCell><StatusChip status={r.status} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View"><IconButton size="small" onClick={() => setViewing(r)} sx={iconBtn('#4f46e5')}><MdVisibility size={17} /></IconButton></Tooltip>{' '}
                      <Tooltip title={pending ? 'Approve' : 'Already decided'}><span><IconButton size="small" disabled={!pending} onClick={() => openDecision(r, 'approved')} sx={iconBtn('#16a34a')}><MdCheckCircle size={18} /></IconButton></span></Tooltip>{' '}
                      <Tooltip title={pending ? 'Reject' : 'Already decided'}><span><IconButton size="small" disabled={!pending} onClick={() => openDecision(r, 'rejected')} sx={iconBtn('#dc2626')}><MdBlock size={17} /></IconButton></span></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} request{meta.total === 1 ? '' : 's'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, pg) => setPage(pg)} shape="rounded" size="small" />
        </Box>
      </Box>

      {/* view */}
      <Dialog open={!!open} onClose={() => setViewing(null)} maxWidth="md" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        {open && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: 'linear-gradient(135deg,#6366f1,#4338ca)', color: '#fff' }}>
              <Avatar variant="rounded" src={img(p.product_images?.[0])} sx={{ width: 52, height: 52, bgcolor: 'rgba(255,255,255,.2)' }}><MdInventory2 /></Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>{p.title || 'Product removed'}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.9 }}>Requested by {seller.name || 'a seller'} · {dt(open.createdAt)}</Typography>
              </Box>
              <StatusChip status={open.status} />
              <IconButton onClick={() => setViewing(null)} size="small" sx={{ color: '#fff' }}><MdClose size={20} /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card title="Price asked vs your catalogue" accent="#f59e0b">
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell sx={head} /><TableCell sx={head} align="right">Seller asks</TableCell><TableCell sx={head} align="right">Catalogue now</TableCell><TableCell sx={head} align="right">Difference</TableCell></TableRow></TableHead>
                    <TableBody>
                      {[['MRP', open.maximumRetailPrice, p.b2c_mrp], ['B2C selling price', open.b2c_sellingPrice, p.b2c_sellingPrice], ['B2B selling price', open.b2b_sellingPrice, p.b2b_sellingPrice]].map(([k, a, c]) => (
                        <TableRow key={k}>
                          <TableCell sx={{ ...cell, fontWeight: 700 }}>{k}</TableCell>
                          <TableCell sx={{ ...cell, fontWeight: 800 }} align="right">{inr(a)}</TableCell>
                          <TableCell sx={cell} align="right">{inr(c)}</TableCell>
                          <TableCell sx={cell} align="right"><Delta asked={a} current={c} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {open.b2c_sellingPrice > open.maximumRetailPrice && <Alert severity="warning" sx={{ mt: 1.5 }}>The B2C price is above the MRP the seller entered.</Alert>}
              </Card>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start' }}>
                <Card title="Seller" accent="#0ea5e9">
                  <Row label="Name" value={seller.name} />
                  <Row label="Business" value={seller.business_name} />
                  <Row label="Mobile" value={seller.mobile} />
                  <Row label="Email" value={seller.email} />
                </Card>
                <Card title="Product" accent="#22c55e">
                  <Row label="UPC" value={p.upc} />
                  <Row label="Ownership" value={p.ownership} />
                  <Row label="Free product" value={p.isFree ? 'Yes' : 'No'} />
                  <Row label="Stock" value={p.availableQuantity !== undefined ? `${p.availableQuantity} available` : null} />
                </Card>
              </Box>
              <Card title="Decision" accent={(STATUS[open.status] || STATUS.pending).accent}>
                <Row label="Status" value={<StatusChip status={open.status} />} />
                <Row label="Requested" value={dt(open.createdAt)} />
                <Row label={open.status === 'pending' ? 'Last updated' : 'Decided'} value={dt(open.updatedAt)} />
                {open.status === 'rejected' && <Row label="Reason given" value={<span style={{ color: '#b91c1c' }}>{open.rejectionReason || '—'}</span>} />}
              </Card>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0', gap: 1 }}>
              <Button onClick={() => setViewing(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
              {open.status === 'pending' && (
                <>
                  <Button onClick={() => openDecision(open, 'rejected')} startIcon={<MdBlock />} sx={{ textTransform: 'none', fontWeight: 800, color: '#dc2626', border: '1px solid #fecaca', borderRadius: '10px' }}>Reject</Button>
                  <Button onClick={() => openDecision(open, 'approved')} startIcon={<MdCheckCircle />} variant="contained" sx={yellowBtn}>Approve</Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* approve / reject */}
      <Dialog open={!!decision} onClose={() => !submitting && setDecision(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        {decision && (
          <>
            <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18 }}>{decision.status === 'approved' ? 'Approve this request?' : 'Reject this request?'}</Typography>
              <Box sx={{ p: 1.75, borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13.5 }}>{decision.request.product_id?.title || 'Product'}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{decision.request.createdBy?.name || 'Seller'} · B2C {inr(decision.request.b2c_sellingPrice)} · B2B {inr(decision.request.b2b_sellingPrice)}</Typography>
              </Box>
              {decision.status === 'approved' ? (
                <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}>The seller gets this product in their catalogue at the prices above, and is notified. This cannot be undone here.</Typography>
              ) : (
                <>
                  <TextField size="small" label="Reason for the seller *" multiline minRows={2} value={reason} onChange={(e) => { setReason(e.target.value); setReasonError(''); }} error={!!reasonError} helperText={reasonError || `${reason.length}/500`} inputProps={{ maxLength: 500 }} />
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {QUICK_REASONS.map((q) => <Chip key={q} size="small" clickable label={q} onClick={() => { setReason(q); setReasonError(''); }} sx={{ fontFamily: FONT, fontWeight: 600 }} />)}
                  </Box>
                </>
              )}
              {actionError && <Alert severity="error">{actionError}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setDecision(null)} disabled={submitting} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
              <Button onClick={confirm} disabled={submitting} variant="contained" sx={decision.status === 'approved' ? yellowBtn : { textTransform: 'none', fontWeight: 800, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>
                {submitting ? 'Saving…' : decision.status === 'approved' ? 'Approve' : 'Reject'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ProductRequestList;
